/**
 * A layout choice as tiles: each tile a small decorative wireframe above its
 * radio + label (the survey builder's Layout, the volunteer page's Layout).
 * The whole tile is the label, so a click anywhere selects it; keyboard focus
 * sits on the radio and rings the tile. The wireframes are decoration
 * (`aria-hidden`); the radio + label carry the meaning. Theme tokens only.
 *
 * The wire pieces (`LayoutWirePage`, `layoutWireLine`) are shared so every
 * picker's sketches read as one family.
 */
import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

export interface LayoutPickerOption<T extends string> {
  value: T;
  label: ReactNode;
  /** The wireframe, drawn inside the tile's hidden sketch area. */
  wire: ReactNode;
}

export interface LayoutPickerProps<T extends string>
  extends Omit<ComponentPropsWithoutRef<typeof RadioGroup>, 'value' | 'onChange' | 'onValueChange' | 'children'> {
  value: T;
  onChange: (value: T) => void;
  options: readonly LayoutPickerOption<T>[];
  /** The id of the field's visible label. */
  labelledBy: string;
}

/** A wireframe text line (sized by the caller's width class). */
export const layoutWireLine = 'h-1.5 rounded-full bg-muted-foreground/40';

/** A small "page", the visitor's screen: portrait by default, sized by `className`. */
export function LayoutWirePage({ children, className }: Readonly<{ children: ReactNode; className?: string }>) {
  return (
    <div
      className={cn(
        'relative flex h-[140px] w-[104px] flex-col gap-2 rounded-md border border-muted-foreground/40 bg-background p-2.5 shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Extra props (the `FormControl` slot's id / aria-describedby / aria-invalid) land on the group. */
export function LayoutPicker<T extends string>({
  value,
  onChange,
  options,
  labelledBy,
  className,
  ...rest
}: Readonly<LayoutPickerProps<T>>) {
  const idBase = useId();
  return (
    <RadioGroup
      {...rest}
      value={value}
      onValueChange={(next) => onChange(next as T)}
      aria-labelledby={labelledBy}
      className={cn('grid max-w-md grid-cols-2 gap-3', className)}
    >
      {options.map((option) => {
        const id = `${idBase}-${option.value}`;
        return (
          <Label
            key={option.value}
            htmlFor={id}
            className={cn(
              'flex cursor-pointer flex-col gap-2 rounded-lg border p-2 font-normal transition-colors hover:bg-muted/30',
              value === option.value && 'border-primary ring-1 ring-primary',
              // Keyboard focus is on the radio inside — ring the whole tile.
              'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
            )}
          >
            <div aria-hidden="true" className="flex h-[164px] items-center justify-center rounded-md bg-muted/50">
              {option.wire}
            </div>
            <span className="flex items-center gap-2 px-0.5">
              <RadioGroupItem value={option.value} id={id} />
              {option.label}
            </span>
          </Label>
        );
      })}
    </RadioGroup>
  );
}
